'use client'

import React, { useMemo } from 'react'
import { useTheme } from '@/shared/theme'
import styles from './feedback.module.css'

export interface EnterpriseSpinnerProps {
  /** Size of the spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number
  /** Variant of the spinner */
  variant?: 'primary' | 'secondary' | 'pulse' | 'ring' | 'dots' | 'bars' | 'orbit' | 'wave'
  /** Speed of the animation */
  speed?: 'slow' | 'normal' | 'fast'
  /** Custom color for the spinner */
  color?: string
  /** Additional className */
  className?: string
  /** Whether to show text label */
  label?: string
  /** Position of the label */
  labelPosition?: 'top' | 'bottom' | 'left' | 'right'
  /** Accessibility label */
  'aria-label'?: string
  /** Whether to show a subtle backdrop */
  withBackdrop?: boolean
  /** Custom backdrop opacity (kept for API compat; CSS uses theme mix) */
  backdropOpacity?: number
}

const SIZE_MAP = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
} as const

const SPEED_MAP = {
  slow: '1.5s',
  normal: '1s',
  fast: '0.6s',
} as const

/**
 * Enterprise spinner — CSS-module animations (no styled-jsx).
 * Theme-aware, reduced-motion safe, WCAG status role.
 */
export function EnterpriseSpinner({
  size = 'md',
  variant = 'primary',
  speed = 'normal',
  color,
  className = '',
  label,
  labelPosition = 'bottom',
  'aria-label': ariaLabel = 'Loading...',
  withBackdrop = false,
}: EnterpriseSpinnerProps) {
  const { token } = useTheme()

  const sizeValue = typeof size === 'number' ? size : SIZE_MAP[size]
  const duration = SPEED_MAP[speed]
  const stroke = sizeValue >= 32 ? 3 : sizeValue >= 24 ? 2.5 : 2

  const spinnerColor = useMemo(() => {
    if (color) return color
    if (variant === 'secondary') return token.colorTextSecondary
    return token.colorPrimary
  }, [color, variant, token])

  const dirClass =
    labelPosition === 'top'
      ? styles.spinnerRootColumnReverse
      : labelPosition === 'bottom'
        ? styles.spinnerRootColumn
        : labelPosition === 'left'
          ? styles.spinnerRootRowReverse
          : styles.spinnerRootRow

  const rootClass = [
    styles.spinnerRoot,
    label ? styles.spinnerRootWithLabel : '',
    dirClass,
    withBackdrop ? styles.spinnerBackdrop : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const cssVars = {
    '--spinner-size': `${sizeValue}px`,
    '--spinner-stroke': String(stroke),
    '--spinner-duration': duration,
    '--spinner-gap': sizeValue >= 32 ? '8px' : '6px',
    color: spinnerColor,
  } as React.CSSProperties

  const renderSpinner = () => {
    const r = Math.max(sizeValue / 2 - stroke, 1)

    switch (variant) {
      case 'pulse':
        return (
          <span className={styles.spinnerBox} aria-hidden="true">
            <span className={styles.pulseDot} />
          </span>
        )

      case 'ring':
        return (
          <span className={styles.spinnerBox} aria-hidden="true">
            <span className={styles.ringWrap}>
              <span className={styles.ringLayer} />
              <span className={styles.ringLayer} />
              <span className={styles.ringLayer} />
            </span>
          </span>
        )

      case 'dots':
        return (
          <span className={`${styles.spinnerBox} ${styles.dotsWrap}`} aria-hidden="true">
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </span>
        )

      case 'bars':
        return (
          <span className={`${styles.spinnerBox} ${styles.barsWrap}`} aria-hidden="true">
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
          </span>
        )

      case 'orbit': {
        const radius = sizeValue / 3
        const cx = sizeValue / 2
        const cy = sizeValue / 2
        const or = sizeValue / 6
        return (
          <span className={styles.spinnerBox} aria-hidden="true">
            <svg className={styles.orbitSvg} viewBox={`0 0 ${sizeValue} ${sizeValue}`}>
              {[0, 1, 2].map((i) => {
                const angle = i * 120 * (Math.PI / 180)
                return (
                  <circle
                    key={i}
                    className={styles.orbitDot}
                    cx={cx + radius * Math.cos(angle)}
                    cy={cy + radius * Math.sin(angle)}
                    r={or}
                  />
                )
              })}
            </svg>
          </span>
        )
      }

      case 'wave':
        return (
          <span className={`${styles.spinnerBox} ${styles.waveWrap}`} aria-hidden="true">
            <span className={styles.waveBar} />
            <span className={styles.waveBar} />
            <span className={styles.waveBar} />
            <span className={styles.waveBar} />
          </span>
        )

      case 'primary':
      case 'secondary':
      default:
        return (
          <span className={styles.spinnerBox} aria-hidden="true">
            <svg className={styles.spinnerSvg} viewBox={`0 0 ${sizeValue} ${sizeValue}`}>
              <circle
                className={styles.spinnerCircle}
                cx={sizeValue / 2}
                cy={sizeValue / 2}
                r={r}
              />
            </svg>
          </span>
        )
    }
  }

  return (
    <div
      className={rootClass}
      style={cssVars}
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      aria-busy="true"
    >
      {renderSpinner()}
      {label ? (
        <span className={`${styles.spinnerLabel} ${sizeValue >= 32 ? styles.spinnerLabelLg : ''}`.trim()}>
          {label}
        </span>
      ) : null}
    </div>
  )
}

export default EnterpriseSpinner
